import React from 'react';
import {
  ButtonBase,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  Stack,
  Typography,
} from '@mui/material';
import { ArrowDropDown as ArrowDropDownIcon } from '@mui/icons-material';
import SearchableSelect from '../../../components/SearchableSelect';
import { DireccionCliente } from '../../../interfaces/DireccionCliente';
import { pickDireccionLabel, truncateLabel } from './planFormHelpers';

export interface VisitRowState {
  nroCliente: number;
  clienteNombre: string;
  codigoDireccion: string;
  direccionLabel: string;
  horarioPreferido: string;
  observaciones: string;
  direcciones: DireccionCliente[];
  direccionLoading: boolean;
  /** Fila cargada desde agenda (se reemplaza al volver a pulsar «Agregar visitas»). */
  fromAgenda?: boolean;
  agendaDias?: import('../../../types/visitasAgenda').VisitasAgendaDias;
}

export const emptyVisitRow = (): VisitRowState => ({
  nroCliente: 0,
  clienteNombre: '',
  codigoDireccion: '',
  direccionLabel: '',
  horarioPreferido: '',
  observaciones: '',
  direcciones: [],
  direccionLoading: false,
});

export const noSpinnerSx = {
  '& input[type=number]': { MozAppearance: 'textfield' },
  '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
    WebkitAppearance: 'none',
    margin: 0,
  },
};

export const visitRowTextFieldProps = {
  size: 'small' as const,
  fullWidth: true,
  FormHelperTextProps: {
    sx: { minHeight: 20, mx: 0 },
  },
};

interface SelectBoxProps {
  label: string;
  display: string;
  placeholder?: string;
  helperText?: string;
  disabled?: boolean;
  onOpen: () => void;
  hideHelper?: boolean;
}

export const SelectBox: React.FC<SelectBoxProps> = ({
  label,
  display,
  placeholder,
  helperText,
  disabled = false,
  onOpen,
  hideHelper = false,
}) => {
  const showPlaceholder = !display;

  return (
    <FormControl fullWidth size="small" disabled={disabled} sx={noSpinnerSx}>
      <InputLabel shrink sx={{ bgcolor: 'background.paper', px: 0.5 }}>
        {label}
      </InputLabel>
      <ButtonBase
        component="div"
        disabled={disabled}
        onClick={() => {
          if (!disabled) onOpen();
        }}
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          px: 1.5,
          minHeight: 40,
          borderRadius: 1,
          border: '1px solid',
          borderColor: disabled ? 'action.disabled' : 'rgba(0, 0, 0, 0.23)',
          bgcolor: disabled ? 'action.hover' : 'background.paper',
          cursor: disabled ? 'default' : 'pointer',
          textAlign: 'left',
          '&:hover': disabled
            ? undefined
            : {
                borderColor: 'text.primary',
              },
        }}
      >
        <Typography
          variant="body2"
          noWrap
          sx={{
            flex: 1,
            color: showPlaceholder ? 'text.secondary' : 'text.primary',
          }}
        >
          {showPlaceholder ? placeholder || 'Seleccionar…' : display}
        </Typography>
        <ArrowDropDownIcon color={disabled ? 'disabled' : 'primary'} fontSize="small" />
      </ButtonBase>
      {!hideHelper ? (
        <FormHelperText sx={{ minHeight: 20, mx: 0 }}>{helperText || ' '}</FormHelperText>
      ) : null}
    </FormControl>
  );
};

export const formatClienteDisplay = (row: VisitRowState): string => {
  if (row.clienteNombre && row.nroCliente) {
    return `${row.nroCliente} — ${row.clienteNombre}`;
  }
  return '';
};

interface PlanVisitaDireccionFieldProps {
  row: VisitRowState;
  onPatch: (patch: Partial<VisitRowState>) => void;
  onOpenDireccionPicker: () => void;
}

export const PlanVisitaDireccionField: React.FC<PlanVisitaDireccionFieldProps> = ({
  row,
  onPatch,
  onOpenDireccionPicker,
}) => {
  const hasCliente = Number(row.nroCliente) > 0;
  const multiple = row.direcciones.length > 1;

  if (multiple) {
    return (
      <Stack direction="row" spacing={0.5} alignItems="flex-start" sx={noSpinnerSx}>
        <SearchableSelect
          label="Dirección"
          size="small"
          allowEmpty
          emptyOptionLabel="Seleccione…"
          value={row.codigoDireccion || null}
          onChange={(v) => {
            const selected = row.direcciones.find((d) => d.codigoDireccion === v);
            if (selected) {
              onPatch({
                codigoDireccion: selected.codigoDireccion || '',
                direccionLabel: pickDireccionLabel(selected),
              });
            } else {
              onPatch({ codigoDireccion: v ?? '', direccionLabel: '' });
            }
          }}
          helperText="El cliente tiene varias direcciones. Elija una del listado."
          options={row.direcciones.map((d) => ({
            value: d.codigoDireccion,
            label: pickDireccionLabel(d),
          }))}
        />
        <IconButton
          size="small"
          color="primary"
          title="Buscar dirección"
          sx={{ mt: 2.75 }}
          onClick={onOpenDireccionPicker}
        >
          <ArrowDropDownIcon />
        </IconButton>
      </Stack>
    );
  }

  const display = row.direccionLoading ? 'Cargando direcciones…' : row.direccionLabel;

  return (
    <SelectBox
      label="Dirección"
      display={display}
      placeholder={
        !hasCliente
          ? 'Indique cliente primero'
          : row.direcciones.length === 0
            ? 'Sin direcciones cargadas'
            : 'Tocá para elegir dirección'
      }
      helperText={
        !hasCliente
          ? 'Seleccione primero el cliente.'
          : row.direccionLoading
            ? ' '
            : row.direcciones.length === 0
              ? 'Sin direcciones cargadas. Tocá para buscar.'
              : row.direccionLabel
                ? 'Dirección del cliente.'
                : 'Tocá el campo para elegir la dirección.'
      }
      disabled={!hasCliente || row.direccionLoading}
      onOpen={onOpenDireccionPicker}
    />
  );
};

export const handleDireccionSelected = (
  d: { codigoDireccion: string; direccion: string },
  onPatch: (patch: Partial<VisitRowState>) => void,
) => {
  const line = [d.codigoDireccion, d.direccion].filter(Boolean).join(' — ');
  onPatch({
    codigoDireccion: d.codigoDireccion,
    direccionLabel: truncateLabel(line),
  });
};
